/**
 * The one loading indicator in this client — `bimgo-ai-web`'s `Loader2`: a ring
 * with a gap, spinning.
 *
 * It replaced a set of pulsing skeleton blocks. Skeletons promise a shape, and
 * three grey bars where a table is about to appear tell the reader something
 * about the layout and nothing about whether anything is happening; the web
 * client has always used a spinner, and having two different "wait" idioms
 * across the two clients was the drift worth removing.
 *
 * The spin is CSS (`.spinner`) and slows right down under
 * `prefers-reduced-motion` rather than stopping — a frozen spinner reads as a
 * hang.
 */
export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg
      className="spinner"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

/**
 * A centred spinner standing in for a block of content, optionally captioned.
 * `role="status"` so a screen reader is told the wait is deliberate.
 */
export function LoadingBlock({ label, size = 32 }: { label?: string; size?: number }) {
  return (
    <div className="loading-block" role="status" aria-label={label ?? "Loading"}>
      <Spinner size={size} />
      {label ? <p>{label}</p> : null}
    </div>
  );
}
